import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasProduct } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getRockwellConfiguredProductsHandler, {
    RockwellConfiguratorRedirectPageParameter,
} from "@tSchaedlerYesco/Handlers/RockwellConfigurator/GetRockwellConfiguredProductsHandler";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    getRockwellConfiguredProductsHandler,
    loadCurrentCart,
    addToCart,
};

const mapStateToProps = () => ({});

type Props = HasProduct & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface RockwellConfiguratorPageControlsStyleCustoms {
    loadingSpinner?: LoadingSpinnerProps;
    buttonsItem?: GridItemProps;
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    loadingSpinnerWrapper?: InjectableCss;
}

const styles: RockwellConfiguratorPageControlsStyleCustoms = {
    loadingSpinnerWrapper: {
        css: css`
            max-width: 100%;
            flex-basis: 100%;
        `,
    },

    loadingSpinner: {
        size: 15,
        css: css``,
    },

    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },

    buttonsItem: {
        width: 12,
        css: css`
            justify-content: center;
            align-items: center;
        `,
    },

    container: {
        css: css`
            padding-bottom: 15px;
            > div[class^="GridOffset"] {
                width: 100%;
                margin: 0 auto;
                padding: 30px 0;
                > div:first-child[class^="GridItemStyle"] {
                    flex-basis: 100%;
                    max-width: 100%;
                    align-content: center;
                    justify-content: center;
                    > p[class^="text"] {
                        text-align: center;
                        width: 100%;
                        font-size: 22px;
                        width: 20%;
                        font-weight: 600;
                        color: rgb(54, 102, 177);
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        width: 65%;
                                        font-size: 18px;
                                    `,
                                    css`
                                        width: 80%;
                                        font-size: 18px;
                                    `,
                                    css`
                                        width: 30%;
                                        font-size: 18px;
                                    `,
                                    css`
                                        font-size: 18px;
                                    `,
                                ],
                                "minMax",
                            )}
                    }
                }
                button[data-test-selector^="makeAnother_selection"] {
                    margin-right: 10px;
                    background: rgb(39, 90, 168);
                    border-color: rgb(39, 90, 168);
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    padding: 0 10px;
                                `,
                                null,
                                null,
                                null,
                            ],
                            "minMax",
                        )}
                    &:hover {
                        background: rgb(0, 0, 255);
                        border-color: rgb(60, 119, 210);
                        span[class^="TypographyStyle"] {
                            color: #ffffff;
                        }
                    }
                    > span {
                        color: #ffffff;
                        font-size: 16px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        font-size: 12px;
                                    `,
                                    null,
                                    null,
                                    null,
                                ],
                                "minMax",
                            )}
                    }
                }
                button[data-test-selector^="createAccount_createButton"] {
                    background: rgb(39, 90, 168);
                    border-color: rgb(39, 90, 168);
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    padding: 0 10px;
                                `,
                                null,
                                null,
                                null,
                            ],
                            "minMax",
                        )}
                    &:hover {
                        background: rgb(0, 0, 255);
                        border-color: rgb(60, 119, 210);
                        span[class^="TypographyStyle"] {
                            color: #ffffff;
                        }
                    }
                    > span {
                        color: #ffffff;
                        font-size: 16px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        font-size: 12px;
                                    `,
                                    null,
                                    null,
                                    null,
                                ],
                                "minMax",
                            )}
                    }
                }
            }
        `,
    },
};

interface State {
    disableAllButtons: boolean;
    loaderMessage: string;
    rockwellConfiguratorURL: string;
}

export class RockwellConfiguratorRedirectPage extends React.Component<Props, State> {
    disableButtons: boolean;

    constructor(props: Props) {
        super(props);
        this.disableButtons = true;
        const loaderMessageToBeDisplayed: any = siteMessage("RockwellConfiguratorLoader_ProductsAreBeingAddedToTheCart")
            ? siteMessage("RockwellConfiguratorLoader_ProductsAreBeingAddedToTheCart")
            : "";
        const rockwellConfiguratorURL: any = siteMessage("RockwellConfiguratorURL");
        this.state = {
            disableAllButtons: true,
            loaderMessage: loaderMessageToBeDisplayed,
            rockwellConfiguratorURL,
        };
    }

    componentDidMount() {
        let cid;
        if (window && window.location && window.location.search) {
            const parsedQuery = parseQueryString<{ cid: string }>(window.location.search);
            cid = parsedQuery.cid;
        }

        const getRockwellConfiguredProductsParameter: RockwellConfiguratorRedirectPageParameter = {
            configurationId: cid || "",
        };

        this.props.getRockwellConfiguredProductsHandler({
            getRockwellConfiguredProductsParameter,
            onSuccess: (result: any) => {
                if (result) {
                    const productCounter = result.result.length;
                    if (result.result.length > 0) {
                        this.addProductToCart(result.result);
                    } else {
                        this.showButtons();
                    }
                } else {
                    this.showButtons();
                }
            },
        });
    }

    async addProductToCart(listOfProducts: any[]) {
        if (listOfProducts.length > 0) {
            const currentIndex: number = listOfProducts.length - 1;
            await this.props.addToCart({
                productId: listOfProducts[currentIndex].id,
                qtyOrdered: listOfProducts[currentIndex].minimumOrderQty,
                unitOfMeasure: listOfProducts[currentIndex].unitOfMeasure,
                notes: listOfProducts[currentIndex].notes,
                onSuccess: (response: any) => {
                    listOfProducts.pop();
                    if (listOfProducts.length === 0) {
                        this.showButtons();
                    } else {
                        this.addProductToCart(listOfProducts);
                    }
                },
                onError: (error: any) => {
                    // eslint-disable-next-line no-console
                    console.log("error", error);
                },
            });
        }
    }

    showButtons() {
        const loaderMessageToBeDisplayed: any = siteMessage("RockwellConfiguratorLoader_ProductsHaveBeenAddedToTheCart")
            ? siteMessage("RockwellConfiguratorLoader_ProductsHaveBeenAddedToTheCart")
            : "";
        this.disableButtons = false;
        this.setState({ disableAllButtons: false });
        this.setState({ loaderMessage: loaderMessageToBeDisplayed });
    }

    render() {
        return (
            <GridContainer {...styles.container}>
                <>
                    <GridItem>
                        <p className="text">{this.state.loaderMessage}</p>
                    </GridItem>

                    {this.state.disableAllButtons && (
                        <GridItem {...styles.loadingSpinnerWrapper}>
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.loadingSpinner} />
                            </StyledWrapper>
                        </GridItem>
                    )}

                    <GridItem {...styles.buttonsItem}>
                        <Button
                            hidden={this.state.disableAllButtons}
                            onClick={this.redirectToRockwellConfigurator}
                            data-test-selector="makeAnother_selection"
                        >
                            {translate("Make another selection")}
                        </Button>
                        <Button
                            hidden={this.state.disableAllButtons}
                            onClick={this.redirectToCartPage}
                            data-test-selector="createAccount_createButton"
                        >
                            {translate("View Cart")}
                        </Button>
                    </GridItem>
                </>
            </GridContainer>
        );
    }

    redirectToRockwellConfigurator = () => {
        const rockwellPageUrl =
            `${this.state.rockwellConfiguratorURL}?redirect_uri=` +
            `${window.location.origin}/RockwellConfiguratorRedirectionPage`;
        window.location.href = rockwellPageUrl;
    };

    redirectToCartPage = () => {
        window.location.href = `${window.location.origin}/Cart`;
    };
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RockwellConfiguratorRedirectPage),
    definition: {
        group: "Product Details",
        displayName: "Rockwell Configurator Redirect Page",
    },
};
export default widgetModule;
